import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, BookOpen, X, ChevronLeft, ChevronRight, Grid, RotateCcw } from 'lucide-react';
import { useData } from '../context/DataContext';
import { fetchQuestions } from '../lib/supabase';
import type { NormalizedQuestion, QuizProgress } from '../lib/types';
import { translations } from '../lib/i18n';
import {
  getSavedQuizProgress,
  saveQuizProgress,
  clearQuizProgress,
  getSavedTheoryOpenDesktop,
  saveTheoryOpenDesktop,
  saveRecentSession,
} from '../lib/storage';
import { QuizQuestion } from '../components/QuizQuestion';
import { QuizResults } from '../components/QuizResults';
import { TheoryPanel } from '../components/TheoryPanel';

export const QuizPage: React.FC = () => {
  const { lessonId = 'all' } = useParams<{ lessonId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const wilaya = searchParams.get('w') || 'all';
  const subjectId = searchParams.get('s') || '';
  const paramTheory = searchParams.get('theory') === '1';

  const { faculties, subjects, lessons, language, loading: metaLoading } = useData();
  const t = translations[language];

  // Screen width tracking for <=900px mobile vs desktop
  const [isMobile, setIsMobile] = useState<boolean>(() =>
    typeof window !== 'undefined' ? window.innerWidth <= 900 : false
  );

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 900);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Current lesson & subject information
  const currentLesson = useMemo(() => {
    if (lessonId === 'all') return null;
    return lessons.find((l) => l.id === lessonId) || null;
  }, [lessons, lessonId]);

  const currentSubject = useMemo(() => {
    if (currentLesson) {
      return subjects.find((s) => s.id === currentLesson.subject_id) || null;
    }
    if (subjectId) {
      return subjects.find((s) => s.id === subjectId) || null;
    }
    return null;
  }, [currentLesson, subjects, subjectId]);

  const currentFaculty = useMemo(() => {
    if (!currentSubject) return null;
    return faculties.find((f) => f.id === currentSubject.faculty_id) || null;
  }, [currentSubject, faculties]);

  const hasTheory = Boolean(currentLesson && currentLesson.has_theory);

  // Theory panel open state
  const [isTheoryOpen, setIsTheoryOpen] = useState<boolean>(() => {
    if (paramTheory) return true;
    if (typeof window !== 'undefined' && window.innerWidth > 900 && hasTheory) {
      return getSavedTheoryOpenDesktop();
    }
    return false;
  });

  const toggleTheory = useCallback(() => {
    setIsTheoryOpen((prev) => {
      const next = !prev;
      if (!isMobile) {
        saveTheoryOpenDesktop(next);
      }
      return next;
    });
  }, [isMobile]);

  // Questions and Quiz State
  const [questions, setQuestions] = useState<NormalizedQuestion[]>([]);
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [selectedIndicesMap, setSelectedIndicesMap] = useState<Record<string, number[]>>({});
  const [submittedMap, setSubmittedMap] = useState<Record<string, boolean>>({});
  const [loadingQuestions, setLoadingQuestions] = useState<boolean>(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [isFinished, setIsFinished] = useState<boolean>(false);
  const [isGridModalOpen, setIsGridModalOpen] = useState<boolean>(false);

  // Progress key
  const progressKey = `${lessonId}_${subjectId}`;

  // Bubbles horizontal scroll ref
  const miniBubblesRef = useRef<HTMLDivElement>(null);

  // Load questions
  const loadQuizQuestions = useCallback(async () => {
    setLoadingQuestions(true);
    setFetchError(null);
    setIsFinished(false);

    try {
      let lessonIds: string[] = [];
      if (lessonId === 'all') {
        if (!currentSubject) {
          throw new Error('Subject ID missing for all questions');
        }
        lessonIds = lessons.filter((l) => l.subject_id === currentSubject.id).map((l) => l.id);
      }

      const rows = await fetchQuestions({
        lessonId,
        subjectId: currentSubject?.id,
        lessonIds,
        wilaya,
      });

      setQuestions(rows);

      // Restore saved progress if any
      const saved = getSavedQuizProgress(progressKey, wilaya);
      if (saved && rows.length > 0) {
        setSelectedIndicesMap(saved.selectedIndices || {});
        setSubmittedMap(saved.isSubmitted || {});
        const safeIndex = Math.min(Math.max(saved.currentIndex || 0, 0), rows.length - 1);
        setCurrentIndex(safeIndex);
      } else {
        setSelectedIndicesMap({});
        setSubmittedMap({});
        setCurrentIndex(0);
      }
    } catch (err) {
      console.error('Error fetching quiz questions:', err);
      setFetchError(err instanceof Error ? err.message : 'Failed to fetch questions');
    } finally {
      setLoadingQuestions(false);
    }
  }, [lessonId, currentSubject, lessons, wilaya, progressKey]);

  useEffect(() => {
    if (!metaLoading) {
      loadQuizQuestions();
    }
  }, [metaLoading, loadQuizQuestions]);

  // Persist progress to localStorage whenever answers or index change
  useEffect(() => {
    if (!loadingQuestions && questions.length > 0) {
      const progress: QuizProgress = {
        selectedIndices: selectedIndicesMap,
        isSubmitted: submittedMap,
        currentIndex,
      };
      saveQuizProgress(progressKey, wilaya, progress);

      // Record in recent sessions for dashboard
      let correct = 0;
      let answered = 0;
      for (const q of questions) {
        if (submittedMap[q.id]) {
          answered++;
          const userSel = selectedIndicesMap[q.id] || [];
          if (
            userSel.length === q.answer_indices.length &&
            userSel.every((idx) => q.answer_indices.includes(idx))
          ) {
            correct++;
          }
        }
      }

      saveRecentSession({
        lessonId,
        lessonName: currentLesson ? currentLesson.name : currentSubject?.name || 'Session',
        subjectName: currentSubject?.name,
        wilaya,
        totalQuestions: questions.length,
        answeredCount: answered,
        correctCount: correct,
        timestamp: Date.now(),
      });
    }
  }, [
    loadingQuestions,
    questions,
    selectedIndicesMap,
    submittedMap,
    currentIndex,
    progressKey,
    wilaya,
    lessonId,
    currentLesson,
    currentSubject,
  ]);

  // Current Question
  const currentQuestion = questions[currentIndex] || null;

  // Auto-scroll active mini bubble into view
  useEffect(() => {
    if (miniBubblesRef.current) {
      const activeBubble = miniBubblesRef.current.querySelector(
        `[data-mini-bubble="${currentIndex}"]`
      ) as HTMLElement | null;
      if (activeBubble) {
        activeBubble.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center',
        });
      }
    }
  }, [currentIndex]);

  // Handle single and multiple choice selection
  const handleToggleOption = (optionIndex: number) => {
    if (!currentQuestion) return;
    const qId = currentQuestion.id;
    if (submittedMap[qId]) return;

    const isMultiple = currentQuestion.max_selections > 1 || currentQuestion.answer_indices.length > 1;

    if (!isMultiple) {
      // Single choice: instant answer and submit
      setSelectedIndicesMap((prev) => ({
        ...prev,
        [qId]: [optionIndex],
      }));
      setSubmittedMap((prev) => ({
        ...prev,
        [qId]: true,
      }));
    } else {
      // Multiple choice: toggle option up to max_selections
      const currentSelected = selectedIndicesMap[qId] || [];
      let nextSelected: number[];

      if (currentSelected.includes(optionIndex)) {
        nextSelected = currentSelected.filter((i) => i !== optionIndex);
      } else {
        if (currentSelected.length < currentQuestion.max_selections) {
          nextSelected = [...currentSelected, optionIndex];
        } else {
          nextSelected = [...currentSelected.slice(1), optionIndex];
        }
      }

      setSelectedIndicesMap((prev) => ({
        ...prev,
        [qId]: nextSelected,
      }));
    }
  };

  const handleValidateMultiple = () => {
    if (!currentQuestion) return;
    const qId = currentQuestion.id;
    const selected = selectedIndicesMap[qId] || [];
    if (selected.length === 0) return;

    setSubmittedMap((prev) => ({
      ...prev,
      [qId]: true,
    }));
  };

  // Keyboard navigation & answering shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        document.activeElement?.tagName === 'INPUT' ||
        document.activeElement?.tagName === 'TEXTAREA'
      ) {
        return;
      }

      if (isFinished || !currentQuestion) return;

      const qId = currentQuestion.id;
      const isSub = submittedMap[qId];

      const num = parseInt(e.key, 10);
      if (!isNaN(num) && num >= 1 && num <= currentQuestion.options.length) {
        if (!isSub) {
          handleToggleOption(num - 1);
        }
        return;
      }

      const letterKey = e.key.toUpperCase();
      if (letterKey >= 'A' && letterKey <= 'J') {
        const letterIndex = letterKey.charCodeAt(0) - 65;
        if (letterIndex >= 0 && letterIndex < currentQuestion.options.length) {
          if (!isSub) {
            handleToggleOption(letterIndex);
          }
          return;
        }
      }

      if (e.key === 'Enter') {
        if (!isSub) {
          const isMultiple =
            currentQuestion.max_selections > 1 || currentQuestion.answer_indices.length > 1;
          if (isMultiple && (selectedIndicesMap[qId] || []).length > 0) {
            handleValidateMultiple();
          }
        } else {
          if (currentIndex < questions.length - 1) {
            setCurrentIndex((idx) => idx + 1);
          } else {
            setIsFinished(true);
          }
        }
        return;
      }

      if (e.key === 'ArrowLeft') {
        if (language === 'ar') {
          if (currentIndex < questions.length - 1) setCurrentIndex((i) => i + 1);
        } else {
          if (currentIndex > 0) setCurrentIndex((i) => i - 1);
        }
      } else if (e.key === 'ArrowRight') {
        if (language === 'ar') {
          if (currentIndex > 0) setCurrentIndex((i) => i - 1);
        } else {
          if (currentIndex < questions.length - 1) setCurrentIndex((i) => i + 1);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    isFinished,
    currentQuestion,
    submittedMap,
    selectedIndicesMap,
    currentIndex,
    questions.length,
    language,
    handleToggleOption,
    handleValidateMultiple,
  ]);

  // Calculate live scores
  const { correctCount, wrongCount, answeredCount } = useMemo(() => {
    let correct = 0;
    let wrong = 0;
    let answered = 0;

    for (const q of questions) {
      if (submittedMap[q.id]) {
        answered++;
        const userSelected = selectedIndicesMap[q.id] || [];
        const correctAnswers = q.answer_indices;

        const isExactMatch =
          userSelected.length === correctAnswers.length &&
          userSelected.every((idx) => correctAnswers.includes(idx));

        if (isExactMatch) {
          correct++;
        } else {
          wrong++;
        }
      }
    }

    return { correctCount: correct, wrongCount: wrong, answeredCount: answered };
  }, [questions, submittedMap, selectedIndicesMap]);

  // Restart actions for Results screen
  const handleRetryInOrder = () => {
    clearQuizProgress(progressKey, wilaya);
    setSelectedIndicesMap({});
    setSubmittedMap({});
    setCurrentIndex(0);
    setIsFinished(false);
  };

  const handleRetryShuffled = () => {
    clearQuizProgress(progressKey, wilaya);
    setSelectedIndicesMap({});
    setSubmittedMap({});
    setQuestions((prev) => [...prev].sort(() => Math.random() - 0.5));
    setCurrentIndex(0);
    setIsFinished(false);
  };

  const handleRetryWrongOnly = () => {
    clearQuizProgress(progressKey, wilaya);
    const wrongQuestions = questions.filter((q) => {
      if (!submittedMap[q.id]) return false;
      const userSelected = selectedIndicesMap[q.id] || [];
      const correctAnswers = q.answer_indices;
      return !(
        userSelected.length === correctAnswers.length &&
        userSelected.every((idx) => correctAnswers.includes(idx))
      );
    });

    if (wrongQuestions.length > 0) {
      setSelectedIndicesMap({});
      setSubmittedMap({});
      setQuestions(wrongQuestions);
      setCurrentIndex(0);
      setIsFinished(false);
    }
  };

  const handleBackToWizard = () => {
    let url = `/?w=${encodeURIComponent(wilaya)}`;
    if (currentFaculty) url += `&f=${encodeURIComponent(currentFaculty.slug)}`;
    if (currentSubject) {
      url += `&y=${encodeURIComponent(currentSubject.department)}&s=${encodeURIComponent(currentSubject.id)}`;
    }
    navigate(url);
  };

  const headerTitle = currentLesson ? currentLesson.name : currentSubject ? currentSubject.name : t.appName;
  const facultyName =
    language === 'ar'
      ? currentFaculty?.name_ar
      : language === 'en'
      ? currentFaculty?.name_en
      : currentFaculty?.name_fr;

  const totalQuestions = questions.length;
  const progressPercent = totalQuestions > 0 ? (answeredCount / totalQuestions) * 100 : 0;

  if (metaLoading || loadingQuestions) {
    return (
      <div className="compact-quiz-viewport">
        <header className="compact-quiz-header">
          <div className="compact-header-inner">
            <button type="button" className="icon-action-btn" onClick={handleBackToWizard}>
              <ArrowLeft size={18} />
            </button>
            <h1 className="compact-heading-title">{t.loadingQuestions}</h1>
            <div style={{ width: 38 }} />
          </div>
        </header>
        <main className="compact-quiz-body">
          <div className="state-box">
            <p className="state-title">{t.loadingQuestions}</p>
          </div>
        </main>
      </div>
    );
  }

  if (fetchError || questions.length === 0) {
    return (
      <div className="compact-quiz-viewport">
        <header className="compact-quiz-header">
          <div className="compact-header-inner">
            <button type="button" className="icon-action-btn" onClick={handleBackToWizard}>
              <ArrowLeft size={18} />
            </button>
            <h1 className="compact-heading-title">{headerTitle}</h1>
            <div style={{ width: 38 }} />
          </div>
        </header>
        <main className="compact-quiz-body">
          <div className="state-box">
            <h2 className="state-title">{fetchError ? t.loadingErrorTitle : t.noLessonsFound}</h2>
            <p className="state-desc">{fetchError || t.loadingErrorDesc}</p>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center' }}>
              <button type="button" className="btn-primary" onClick={loadQuizQuestions}>
                {t.retry}
              </button>
              <button type="button" className="btn-ghost" onClick={handleBackToWizard}>
                {t.backToWizard}
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (isFinished) {
    const skippedCount = totalQuestions - answeredCount;
    return (
      <div className="compact-quiz-viewport">
        <header className="compact-quiz-header">
          <div className="compact-header-inner">
            <button type="button" className="icon-action-btn" onClick={handleBackToWizard}>
              <ArrowLeft size={18} />
            </button>
            <h1 className="compact-heading-title">{headerTitle}</h1>
            <button type="button" className="icon-action-btn" onClick={handleBackToWizard}>
              <X size={18} />
            </button>
          </div>
        </header>
        <main className="compact-quiz-body">
          <QuizResults
            total={totalQuestions}
            correctCount={correctCount}
            wrongCount={wrongCount}
            skippedCount={skippedCount}
            onRetryInOrder={handleRetryInOrder}
            onRetryShuffled={handleRetryShuffled}
            onRetryWrongOnly={handleRetryWrongOnly}
            onBackToLessons={handleBackToWizard}
          />
        </main>
      </div>
    );
  }

  const selectedForCurrent = (currentQuestion && selectedIndicesMap[currentQuestion.id]) || [];
  const isCurrentSubmitted = Boolean(currentQuestion && submittedMap[currentQuestion.id]);

  return (
    <div className="compact-quiz-viewport">
      {/* =======================================================
          TOP BAR HEADER (COMPACT & CLEAN)
          ======================================================= */}
      <header className="compact-quiz-header">
        <div className="compact-header-inner">
          <div className="compact-header-start">
            <button
              type="button"
              className="icon-action-btn"
              onClick={handleBackToWizard}
              title={t.backToWizard}
            >
              <ArrowLeft size={18} />
            </button>
            <div className="compact-header-title-box">
              <h1 className="compact-heading-title" dir="auto">
                {headerTitle}
              </h1>
              {currentSubject && currentLesson && (
                <span className="compact-subheading-name">{currentSubject.name}</span>
              )}
            </div>
          </div>

          <div className="compact-header-end">
            <span className="score-pill correct" aria-label={`${correctCount} correct`}>
              ✓ {correctCount}
            </span>
            <span className="score-pill wrong" aria-label={`${wrongCount} wrong`}>
              ✗ {wrongCount}
            </span>

            {hasTheory && (
              <button
                type="button"
                className={`icon-action-btn ${isTheoryOpen ? 'active' : ''}`}
                onClick={toggleTheory}
                title={t.theoryTooltip}
              >
                <BookOpen size={17} />
              </button>
            )}

            <button
              type="button"
              className="icon-action-btn"
              onClick={handleBackToWizard}
              title={t.backToWizard}
            >
              <X size={17} />
            </button>
          </div>
        </div>
      </header>

      {/* Progress Bar directly under header */}
      <div className="compact-progress-wrap">
        <div className="compact-progress-fill" style={{ width: `${progressPercent}%` }} />
      </div>

      {/* =======================================================
          QUESTION VIEWPORT (VIEW-FIT, ELIMINATES VERTICAL CHURN)
          ======================================================= */}
      <div
        className={`compact-quiz-grid ${
          hasTheory && isTheoryOpen && !isMobile ? 'has-theory-desktop' : ''
        }`}
        style={
          {
            '--pane-dir': language === 'ar' ? 'rtl' : 'ltr',
          } as React.CSSProperties
        }
      >
        <main className="compact-question-pane">
          {currentQuestion && (
            <QuizQuestion
              question={currentQuestion}
              currentIndex={currentIndex}
              totalQuestions={totalQuestions}
              selectedIndices={selectedForCurrent}
              isSubmitted={isCurrentSubmitted}
              facultyName={facultyName}
              lessonName={currentLesson?.name}
              onToggleOption={handleToggleOption}
              onValidate={handleValidateMultiple}
            />
          )}
        </main>

        {hasTheory && isTheoryOpen && (
          <div className="compact-theory-pane">
            <TheoryPanel
              lessonId={currentLesson!.id}
              isMobile={isMobile}
              onClose={() => setIsTheoryOpen(false)}
            />
          </div>
        )}
      </div>

      {/* =======================================================
          SLIM STICKY FOOTER TOOLBAR (ONE-ROW, ERGONOMIC)
          ======================================================= */}
      <footer className="compact-sticky-bar">
        <div className="compact-bar-inner">
          {/* Previous Button */}
          <button
            type="button"
            className="compact-nav-btn"
            onClick={() => setCurrentIndex((idx) => Math.max(0, idx - 1))}
            disabled={currentIndex === 0}
          >
            <ChevronLeft size={18} />
            <span className="compact-btn-text">{t.previous}</span>
          </button>

          {/* Center Counter & Bubble Grid trigger */}
          <div className="compact-center-group">
            <button
              type="button"
              className="compact-index-badge"
              onClick={() => setIsGridModalOpen(true)}
              title={t.jumpToQuestion}
            >
              <Grid size={14} />
              <span>
                {currentIndex + 1} / {totalQuestions}
              </span>
            </button>

            {/* Micro inline preview of nearby question bubbles */}
            <div className="compact-mini-strip" ref={miniBubblesRef}>
              {questions.map((q, idx) => {
                const isCur = idx === currentIndex;
                const isSub = submittedMap[q.id];
                let dotClass = '';
                if (isSub) {
                  const userSel = selectedIndicesMap[q.id] || [];
                  const isExact =
                    userSel.length === q.answer_indices.length &&
                    userSel.every((i) => q.answer_indices.includes(i));
                  dotClass = isExact ? 'ok' : 'err';
                }

                return (
                  <button
                    key={q.id}
                    type="button"
                    data-mini-bubble={idx}
                    className={`mini-strip-dot ${dotClass} ${isCur ? 'active' : ''}`}
                    onClick={() => setCurrentIndex(idx)}
                    title={`Question ${idx + 1}`}
                  />
                );
              })}
            </div>
          </div>

          {/* Next or Finish Button */}
          {currentIndex < totalQuestions - 1 ? (
            <button
              type="button"
              className="compact-primary-btn"
              onClick={() => setCurrentIndex((idx) => Math.min(totalQuestions - 1, idx + 1))}
            >
              <span>{t.next}</span>
              <ChevronRight size={18} />
            </button>
          ) : (
            <button
              type="button"
              className="compact-primary-btn finish"
              onClick={() => setIsFinished(true)}
            >
              <span>{t.finish}</span>
            </button>
          )}
        </div>
      </footer>

      {/* =======================================================
          GRID POPOVER MODAL (FOR DIRECT JUMP TO ANY QUESTION)
          ======================================================= */}
      {isGridModalOpen && (
        <div
          className="question-grid-backdrop"
          onClick={() => setIsGridModalOpen(false)}
        >
          <div
            className="question-grid-modal"
            onClick={(e) => e.stopPropagation()}
            dir="auto"
          >
            <div className="question-grid-header">
              <span className="question-grid-title">{t.jumpToQuestion}</span>
              <button
                type="button"
                className="icon-action-btn"
                style={{ width: 32, height: 32 }}
                onClick={() => setIsGridModalOpen(false)}
              >
                <X size={16} />
              </button>
            </div>

            <div className="question-grid-body">
              {questions.map((q, idx) => {
                const isCurrent = idx === currentIndex;
                const isSub = submittedMap[q.id];
                let cellClass = '';

                if (isSub) {
                  const userSel = selectedIndicesMap[q.id] || [];
                  const isExact =
                    userSel.length === q.answer_indices.length &&
                    userSel.every((i) => q.answer_indices.includes(i));
                  cellClass = isExact ? 'correct' : 'wrong';
                }

                return (
                  <button
                    key={q.id}
                    type="button"
                    className={`grid-cell-btn ${cellClass} ${isCurrent ? 'current' : ''}`}
                    onClick={() => {
                      setCurrentIndex(idx);
                      setIsGridModalOpen(false);
                    }}
                  >
                    {idx + 1}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
